/**
 * The query grammar of API convention 2: how every BMS list is paged and
 * filtered, in one place so two leaves cannot spell `limit` differently.
 *
 * BROWSER-SAFE: `effect` plus the pure cursor codec.
 *
 * EVERY FILTER IS `S.optional(S.String)` ON THE WIRE, and the handler decodes
 * it. URL parameters are strings; a schema that claims otherwise is claiming
 * the transport does something it does not. The decode happens in the handler
 * so a bad filter is a 400 naming the parameter rather than a 404 from a route
 * that did not match.
 *
 * UNKNOWN QUERY PARAMETERS ARE REJECTED, and that is not this module's doing:
 * a group's URL parameters schema is a strict struct (`strictDecoding` from
 * public-api), so `?limitt=10` is a 400 with the path in `details[]` rather
 * than a silently ignored typo that returns the wrong page. A dropped filter
 * on a list of a broker's clients is worse than an error, because it returns
 * MORE than was asked for.
 *
 * THE `sort` PARAMETER IS A CLOSED SET PER SURFACE, and this is the ONE
 * amendment to convention 2. The convention's stated reason for refusing a
 * sort was that "a caller-chosen sort over a keyset cursor is a different
 * cursor grammar per sort". That is correct, and it is the design rather than
 * the objection: a surface publishes a closed set of orders and each member is
 * declared BESIDE its own cursor key schema in
 * `../infrastructure/order-specs.ts`, so an order without a cursor grammar
 * does not exist as a value. `listParams` renders the set as
 * `S.Literal(...spec.sort)`, so an unknown order is a 400 naming the parameter
 * through the strict-decoding path this module already relies on. The second
 * half of the convention's reason survives untouched: the order a register
 * renders in is a product decision, the product decides the SET, and a caller
 * picks from it. Orders are on RAW timestamp columns, never `date_trunc`.
 *
 * The server still owns the order, the filter, the page, the total and the
 * aggregates. The browser renders.
 */
import { ParseResult, Schema as S } from "effect"

export {
  clampPageLimit,
  DEFAULT_PAGE_LIMIT,
  MAX_PAGE_LIMIT,
  makeKeysetCursorCodec,
  InvalidKeysetCursorError,
  withKeysetCursorOrder,
  keysetOrderStamp,
  keysetCursorOrderOf,
} from "@/shared/lib/keyset-cursor"

/**
 * Keyset paging, never offset. `cursor` is opaque by contract and `limit` is
 * CLAMPED to the published bounds rather than rejected: a hopeful
 * `?limit=100000` still returns a page.
 */
export const PageParams = S.Struct({
  cursor: S.optional(S.String),
  limit: S.optional(S.NumberFromString),
})
export type PageParams = typeof PageParams.Type

/**
 * The page envelope every list returns. `nextCursor` is null on the last page,
 * which is the only end-of-list signal: an empty `items` array is also a valid
 * last page and a caller must not need a count to know it is finished.
 */
export const page = <A, I, R>(itemSchema: S.Schema<A, I, R>) =>
  S.Struct({
    items: S.Array(itemSchema),
    nextCursor: S.NullOr(S.String),
  })

/**
 * Archived rows are hidden unless asked for (convention 2). Deleted rows never
 * appear at all, on any value of this.
 */
export const IncludeArchived = S.Struct({
  includeArchived: S.optional(S.String),
})
export type IncludeArchived = typeof IncludeArchived.Type

/**
 * THE SHORTEST TERM A SEARCH IS RUN FOR (Q36, ruled 2026-11-02, review
 * finding 12). A term of one or two characters matches most of a firm's book
 * and costs the most to answer: 75 to 542 ms of statements per short keystroke
 * at 100,000 clients across Clients, contacts, Leads, quotes and Tasks
 * (query-speed research 19 §5.4). So no lane searches under three.
 */
export const SEARCH_TERM_MIN_LENGTH = 3

/**
 * The term a search runs for: trimmed, and undefined when it is empty or
 * shorter than `SEARCH_TERM_MIN_LENGTH`.
 */
export const searchTextOf = (raw: string | null | undefined): string | undefined => {
  if (raw === null || raw === undefined) return undefined
  const trimmed = raw.trim()
  return trimmed.length >= SEARCH_TERM_MIN_LENGTH ? trimmed : undefined
}

/**
 * A term of one or two characters once trimmed: the term `SearchTerm` refuses.
 * Empty is not refused, it is simply no search.
 */
export const isRefusedSearchTerm = (raw: string | null | undefined): boolean => {
  if (raw === null || raw === undefined) return false
  const length = raw.trim().length
  return length > 0 && length < SEARCH_TERM_MIN_LENGTH
}

/**
 * EVERY FREE-TEXT FIELD OF A BMS CONTRACT IS THIS (RT4 (a),
 * `bms-search-term-ratchet`). An optional URL parameter or payload field that
 * trims, and DECODES A TERM OF ONE OR TWO CHARACTERS AS ABSENT.
 *
 * WHAT EACH LANE SEES (Q36):
 *
 * - THE INTERNAL LANE answers the unfiltered list, because the field decodes
 *   as `undefined`. No handler does anything: every handler that forwards its
 *   `urlParams` forwards no search.
 * - THE PUBLIC FIRM-KEY LANE answers a 400 `invalid_request` naming the
 *   parameter. The endpoints are the same objects on both lanes, so the
 *   public handlers check the raw parameter before the service runs
 *   (`shared/presentation/public-search-term.ts`).
 * - THE AGENT's `search` targets answer in band and read nothing
 *   (`insurance-broker-bms-agent.ts`).
 *
 * AN EXACT LOOKUP (a policy number, a code) is not free text, keeps any length
 * and does not use this.
 *
 * `maxLength` keeps a field's existing cap on the wire; a field that had none
 * takes none. The cap is checked before the trim, as it was.
 *
 * WHY THE TRANSFORM SITS INSIDE `S.optional` AND THE KEY STAYS. The field's
 * type is `string | undefined` on both sides, exactly what `S.optional(S.String)`
 * gave, and the struct stays a plain struct. A key-level transformation
 * (`S.optionalToOptional`) would turn every struct holding one into a
 * transformation, whose encoded side drops the other fields' property
 * annotations: the published document lost a parameter's description that
 * way. The encode refuses `undefined` so the optional's own `undefined` member
 * takes it, and a client never sends an empty `q=`.
 */
export const searchTerm = (maxLength?: number) =>
  S.optional(
    S.transformOrFail(maxLength === undefined ? S.String : S.String.pipe(S.maxLength(maxLength)), S.UndefinedOr(S.String), {
      strict: true,
      decode: (raw) => ParseResult.succeed(searchTextOf(raw)),
      encode: (term, _, ast) =>
        term === undefined ? ParseResult.fail(new ParseResult.Forbidden(ast, term, "no search term to encode")) : ParseResult.succeed(term),
    }),
  )

/** `searchTerm()` with no cap, the shape of most search fields. */
export const SearchTerm = searchTerm()

/**
 * `SearchTerm` for a REQUIRED nullable payload field (an RPC's
 * `search: string | null`): null, empty and a term of one or two characters
 * all decode as null, which every such reader takes as no search.
 */
export const SearchTermOrNull = S.transform(S.NullOr(S.String), S.NullOr(S.String), {
  strict: true,
  decode: (raw) => searchTextOf(raw) ?? null,
  encode: (term) => term,
})

/** Free text over whatever the leaf's contract docblock says it searches. */
export const FreeText = S.Struct({
  q: SearchTerm,
})
export type FreeText = typeof FreeText.Type

/** The sync filter: rows changed at or after this instant. */
export const UpdatedSince = S.Struct({
  updatedSince: S.optional(S.String),
})
export type UpdatedSince = typeof UpdatedSince.Type

/**
 * `includeArchived=true` and nothing else. Absent, empty and any other value
 * are false, so a caller cannot widen a list by typo.
 */
export const isTrueFlag = (value: string | undefined): boolean => value === "true"

/**
 * `total=false` and nothing else declines the set-wide count. Absent, empty and
 * any other value COUNT.
 *
 * THE OPPOSITE POLARITY TO `isTrueFlag`, DELIBERATELY, AND THIS IS THE ONE
 * PLACE THE REASON IS WRITTEN. The nine surfaces that read this cite the
 * paragraph rather than repeating it.
 *
 * THE DECIDING FACT IS THAT EVERY CALLER TODAY ALREADY RECEIVES THE COUNT.
 * Those nine list surfaces shipped with `total: true` written as a literal
 * below the wire, so the count is not something a caller opted into: it is
 * simply what the endpoint answers, and no caller sends a flag because there
 * was none to send. Adding the parameter and reading it with `isTrueFlag` would
 * therefore take the field away from every existing caller at the commit that
 * added it, silently, on a request none of them changed. Absent has to keep
 * meaning counted for the parameter to be additive at all.
 *
 * THE FIRM-KEY LANE DOES NOT SERVE THESE NINE METHODS TODAY, and an earlier
 * version of this paragraph leant on the claim that it did. MEASURED
 * 2026-09-14: `src/app/api/v1/**` mounts only the `*PublicApiLive` groups, and
 * those call `list`, `get` and the per-record sub-lists rather than the view,
 * board and register page methods changed here; leads, audit, catalogues,
 * insurer registry and referral partners publish no `presentation/public`
 * folder at all. The rule above does not depend on that either way. It is the
 * rule the lane WOULD need if one of these ever went public, because a
 * published field that disappears is a breaking change wherever the caller sits,
 * and it is already the right rule for the internal callers that exist.
 *
 * SO THE BROWSER BUYS THE SAVING WHERE IT IS MEASURED. `useBmsList` sends
 * `false` on the pages that do not need the number, and a caller that says
 * nothing keeps the answer it has always had.
 *
 * ONE READING FOR THE WHOLE BOOK, RULED 2026-09-19. An earlier version of this
 * paragraph carved out a new surface, which was allowed to publish
 * `isTrueFlag(input.total)` because nothing relied on its count yet, and the
 * tasks register took that carve-out. The result was one parameter name meaning
 * two things across the book: nine surfaces counted when asked nothing and the
 * register did not, so a firm-key caller had to know which list it was talking
 * to before it could read its own answer. Tai's ruling on decision D3 of the
 * hardening follow-ups is that the register aligns to the nine, and the
 * carve-out is gone: EVERY list that publishes `total` reads it through this
 * function, whether or not it has shipped a count before.
 *
 * THE SAVING IS BOUGHT AT THE CALLER, NOT AT THE DEFAULT. A count over a firm's
 * partition is still a scan, and the envelope's own docblock still says the door
 * is opt-in. What pays for it is `totalParam` in `use-bms-list.ts`, which sends
 * an explicit `true` or `false` on every page rather than leaving the parameter
 * off, so the browser declines the count on every page after the first and the
 * default never decides anything for a screen.
 */
export const isNotFalseFlag = (value: string | undefined): boolean => value !== "false"

/**
 * Every declared filter key as `S.optional(S.String)`, which is the rule this
 * module's own header states: URL parameters are strings and the handler
 * decodes them. A `filterFields` that emitted anything else would contradict a
 * comment in its own file.
 */
const optionalFilter = () => S.optional(S.String)
type OptionalFilter = ReturnType<typeof optionalFilter>

/**
 * The four parameter names `listParams` owns. A surface's filter may not be
 * called any of them: `filterFields` is SPREAD into the struct after them, so a
 * filter called `limit` would overwrite `S.NumberFromString` with
 * `S.optional(S.String)` and the page size would arrive as a string that
 * `clampPageLimit` reads as not finite, silently falling back to the default.
 * A collision is a code bug in a surface's own contract, so it throws at module
 * load rather than at request time.
 */
const RESERVED_PARAMS = ["sort", "cursor", "limit", "aggregates"] as const

export const filterFields = <Filter extends string>(
  filters: ReadonlyArray<Filter>,
): { readonly [K in Filter]: OptionalFilter } => {
  for (const key of filters) {
    if ((RESERVED_PARAMS as ReadonlyArray<string>).includes(key)) {
      throw new Error(
        `listParams: "${key}" is one of the four parameters the query grammar owns (${RESERVED_PARAMS.join(", ")}), so a surface cannot publish a filter by that name`,
      )
    }
  }
  return Object.fromEntries(filters.map((key) => [key, optionalFilter()])) as {
    readonly [K in Filter]: OptionalFilter
  }
}

/**
 * One list endpoint's URL parameters, from the three closed sets the surface
 * publishes: the orders it offers, the filter keys it offers, and the
 * aggregates it can compute.
 *
 * `sort` IS A NON-EMPTY TUPLE. `readonly [Sort, ...Sort[]]` rather than
 * `readonly Sort[]`, so a surface cannot publish an empty order set: a list
 * with no order has no cursor grammar and `S.Literal()` with no member is a
 * schema nothing decodes.
 *
 * THE FILTER STRUCT IS EXHAUSTIVE AND CLOSED over exactly the keys the surface
 * declared. That is what turns a dropped control into a compile error on the
 * hook side rather than a filter the screen offers and the request forgets.
 *
 * `aggregates` IS A CLOSED SET TOO, for the same reason as the sort and by the
 * same mechanism: a caller names an aggregate set this surface can actually
 * compute, and anything else is a 400 naming the parameter. A surface that
 * publishes NO aggregates passes `[]`, which renders as `S.Never`, so the
 * parameter may be absent and may not carry a value. The set is all-or-nothing
 * rather than a comma list, because `listPage` computes one aggregate spec in
 * one statement.
 */
export const listParams = <Sort extends string, Filter extends string, Aggregate extends string>(
  spec: {
    readonly sort: readonly [Sort, ...Sort[]]
    readonly filters: ReadonlyArray<Filter>
    readonly aggregates: ReadonlyArray<Aggregate>
  },
) =>
  S.Struct({
    sort: S.optional(S.Literal(...spec.sort)),
    cursor: S.optional(S.String),
    limit: S.optional(S.NumberFromString),
    aggregates: S.optional(S.Literal(...spec.aggregates)),
    ...filterFields(spec.filters),
  })

/**
 * The only shape a BMS list endpoint may return.
 *
 * `items` KEEPS ITS NAME. 47 of the 100 GET endpoints already return
 * `page(itemSchema)`'s `{ items, nextCursor }` and 19 of those are registered
 * on the public firm-key lane from the same shared definition, published
 * unauthenticated with a committed conformance snapshot, so renaming the field
 * is a breaking change to a published API. The three added fields are additive
 * and safe.
 *
 * `truncated` IS REQUIRED AND IS NEVER DERIVED BY THE CALLER. It is Stripe's
 * `has_more` field for field. `clients-service.ts:1958-1961` returns
 * `nextCursor: null` unconditionally after cutting at 2,000 rows and no caller
 * can tell; under this envelope no handler computes either field, because
 * `listPage` fetches `limit + 1` and sets both from the same fact.
 *
 * `total` AND `aggregates` ARE NULL UNLESS ASKED FOR, and both are computed
 * over the SET rather than over the page. A count over a firm's partition is a
 * scan, which is why the door is opt-in rather than always open.
 *
 * `limit` ECHOES THE PAGE SIZE THE SERVER APPLIED, which is how the clamp
 * stops being silent. `clampPageLimit` answers 500 for `?limit=100000` and
 * 100 for a limit that was never sent, and until now the envelope said
 * neither, so a caller reading 500 rows back could not tell whether the set
 * ended or the server cut. The bound is made LOUD, never raised.
 *
 * WHY THE APPLIED VALUE AND NOT A `clamped` FLAG. The flag can only be
 * computed where the raw request is still in hand, and it is not: 48 call
 * sites clamp with `clampPageLimit` BEFORE `listPage` sees the number, so a
 * flag set inside the primitive would read false on exactly the requests that
 * were cut. The applied value is correct wherever it is set, and the caller
 * compares it with the limit it sent, which it has.
 *
 * `limit` IS REQUIRED, LIKE THE OTHER FIVE, and it shipped optional for about
 * an hour on 2026-09-13 while the surfaces that build this envelope by hand
 * were brought up to it. Optional would have meant the clamp was visible on
 * the surfaces served by `listPage` and invisible on the others, which is a
 * field a caller cannot rely on and therefore will not read. Every surface
 * that pages by hand already holds the clamped size, so it was one line each.
 */
export const listEnvelope = <A, I, R, Agg, AggI>(
  item: S.Schema<A, I, R>,
  aggregates: S.Schema<Agg, AggI>,
) =>
  S.Struct({
    items: S.Array(item),
    nextCursor: S.NullOr(S.String),
    /** True when the server stopped at the limit. NEVER derived by the caller. */
    truncated: S.Boolean,
    /** Null unless the caller asked for it: a count over the set is a scan. */
    total: S.NullOr(S.Int),
    /** Null unless the caller asked for them; computed over the SET, not the page. */
    aggregates: S.NullOr(aggregates),
    /** The page size the server APPLIED, which is how the clamp is visible. */
    limit: S.Int,
  })

/**
 * The decoded envelope, as a repository returns it and a hook consumes it.
 *
 * Six fields and nothing else: a seventh would be a figure somebody derived on
 * one side of the wire and the other side could not check. The sixth, `limit`,
 * is not derived: it is the page size the server applied, reported by the one
 * primitive that applies it.
 */
export interface BmsListEnvelope<Row, Agg> {
  readonly items: ReadonlyArray<Row>
  readonly nextCursor: string | null
  readonly truncated: boolean
  readonly total: number | null
  readonly aggregates: Agg | null
  /** The applied page size. Required: a page the caller cannot size is the defect. */
  readonly limit: number
}
